import * as React from 'react';
import { CalendarDaysIcon, ChevronDownIcon, XIcon } from 'lucide-react';

import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { InputGroup, InputGroupButton } from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  buildDateFromSegments,
  buildDateTimeSegmentDefs,
  buildTimeString,
  combineDateAndTime,
  type DateTimeSegmentKey,
  type DateTimeSegmentState,
  EMPTY_DATE_TIME_SEGMENTS,
  formatTimeFromDate,
  segmentsFromDate,
  segmentsFromTime,
} from './date-time-utils';
import {
  hasAnySegmentValue,
  resolveMonthDisplay,
  resolveMonthSegmentKeyAction,
  resolveNumericSegmentKeyAction,
  SEGMENT_INPUT_CLASS_NAME,
  SEGMENT_NAVIGATION_ALLOWED_KEYS,
  segmentWidthPx,
} from './segmented-input-utils';
import {
  parseTime,
  segmentsFromTimeParts,
  timePartsFromSegments,
  type TimeParts,
} from '@/components/ui/shadcn/custom/TimePicker/time-utils';
import {
  TimePicker,
  type TimePopoverLayout,
  type TimePrecision,
} from '@/components/ui/shadcn/custom/TimePicker/time-picker';
import { cn } from 'cn';
import { mergeRefs } from '@/utils/mergeRefs';

type DateTimePickerVariant =
  | 'columns'
  | 'single-column-30min'
  | 'four-column-30min';

type DateTimePickerProps = Omit<
  React.ComponentPropsWithoutRef<'div'>,
  'id' | 'className' | 'children' | 'aria-invalid'
> & {
  id?: string;
  dateTime?: Date;
  defaultDateTime?: Date;
  onValueChange?: (value: Date | undefined) => void;
  className?: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  captionLayout?: React.ComponentProps<typeof Calendar>['captionLayout'];
  navigationVariant?: React.ComponentProps<
    typeof Calendar
  >['navigationVariant'];
  fixedWeeks?: React.ComponentProps<typeof Calendar>['fixedWeeks'];
  use12Hour?: boolean;
  timeVariant?: DateTimePickerVariant;
  monthFormat?: 'long' | 'numeric';
};

type VariantConfig = {
  precision: TimePrecision;
  popoverLayout: TimePopoverLayout;
  step: number;
};

const VARIANT_CONFIG: Record<DateTimePickerVariant, VariantConfig> = {
  columns: {
    precision: 'minute',
    popoverLayout: 'columns',
    step: 1,
  },
  'single-column-30min': {
    precision: 'minute',
    popoverLayout: 'single-column-list',
    step: 1800,
  },
  'four-column-30min': {
    precision: 'minute',
    popoverLayout: 'four-column-grid',
    step: 1800,
  },
};

const DateTimePicker = React.forwardRef<HTMLDivElement, DateTimePickerProps>(
  function DateTimePicker(
    {
      id,
      dateTime,
      defaultDateTime,
      onValueChange,
      className,
      disabled = false,
      'aria-invalid': externalInvalid,
      captionLayout = 'dropdown',
      navigationVariant = 'standard',
      fixedWeeks = false,
      use12Hour = false,
      timeVariant = 'columns',
      monthFormat = 'long',
      ...rest
    },
    forwardedRef
  ) {
    const variantConfig = VARIANT_CONFIG[timeVariant];
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const inputGroupRef = React.useRef<HTMLDivElement>(null);
    const [calendarHeight, setCalendarHeight] = React.useState<number>();
    const calendarResizeObserverRef = React.useRef<ResizeObserver | null>(null);
    const calendarSettleFrameRef = React.useRef<number | null>(null);

    // Tracks the calendar's rendered height (which varies by month row count) so the
    // time panel can match it.
    const calendarWrapperRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        calendarResizeObserverRef.current?.disconnect();
        calendarResizeObserverRef.current = null;
        if (calendarSettleFrameRef.current !== null) {
          cancelAnimationFrame(calendarSettleFrameRef.current);
          calendarSettleFrameRef.current = null;
        }

        if (!node) return;

        const updateHeight = () =>
          setCalendarHeight(node.getBoundingClientRect().height);
        updateHeight();

        // The popover's open transition (zoom-in-95, duration-100) is still scaling the
        // calendar in when this ref first fires, so the very first measurement can read a
        // smaller, mid-animation height. Keep re-measuring for a few frames past the
        // transition so the panel settles on the calendar's true final height.
        let framesLeft = 12;
        const settleDuringTransition = () => {
          updateHeight();
          framesLeft -= 1;
          calendarSettleFrameRef.current =
            framesLeft > 0
              ? requestAnimationFrame(settleDuringTransition)
              : null;
        };
        calendarSettleFrameRef.current = requestAnimationFrame(
          settleDuringTransition
        );

        const observer = new ResizeObserver(updateHeight);
        observer.observe(node);
        calendarResizeObserverRef.current = observer;
      },
      []
    );

    const [internalDate, setInternalDate] = React.useState<Date | undefined>(
      () => {
        const seed = dateTime ?? defaultDateTime;
        return seed ? new Date(seed) : undefined;
      }
    );
    const [timeValue, setTimeValue] = React.useState<string>(() => {
      const seed = dateTime ?? defaultDateTime;
      if (!seed) return '';
      return formatTimeFromDate(seed, use12Hour, variantConfig.precision);
    });

    React.useEffect(() => {
      if (!timeValue) return;

      const parsedTime = parseTime(timeValue);
      if (!parsedTime) return;

      const dateForFormat = new Date(0);
      dateForFormat.setHours(
        parsedTime.hour24,
        parsedTime.minute,
        parsedTime.second,
        0
      );

      const nextFormattedValue = formatTimeFromDate(
        dateForFormat,
        use12Hour,
        variantConfig.precision
      );

      if (nextFormattedValue !== timeValue) {
        setTimeValue(nextFormattedValue);
      }
    }, [timeValue, use12Hour, variantConfig.precision]);

    const emitChange = React.useCallback(
      (nextDate: Date | undefined, nextTime: string | undefined) => {
        onValueChange?.(combineDateAndTime(nextDate, nextTime));
      },
      [onValueChange]
    );

    const [segments, setSegments] = React.useState<DateTimeSegmentState>(
      () => ({
        ...EMPTY_DATE_TIME_SEGMENTS,
        ...segmentsFromDate(internalDate),
        ...segmentsFromTime(timeValue, use12Hour, variantConfig.precision),
      })
    );
    const [hasInvalidDate, setHasInvalidDate] = React.useState(false);
    const [monthTextBuffer, setMonthTextBuffer] = React.useState('');

    const timePartsForPopover = React.useMemo(
      () => timePartsFromSegments(segments, use12Hour, variantConfig.precision),
      [segments, use12Hour, variantConfig.precision]
    );

    const segmentDefs = React.useMemo(
      () => buildDateTimeSegmentDefs(use12Hour),
      [use12Hour]
    );

    const segmentOrder = React.useMemo<DateTimeSegmentKey[]>(() => {
      const order: DateTimeSegmentKey[] = [
        'month',
        'day',
        'year',
        'hour',
        'minute',
      ];
      if (variantConfig.precision === 'second') order.push('second');
      if (use12Hour) order.push('period');
      return order;
    }, [variantConfig.precision, use12Hour]);

    const monthRef = React.useRef<HTMLInputElement>(null);
    const dayRef = React.useRef<HTMLInputElement>(null);
    const yearRef = React.useRef<HTMLInputElement>(null);
    const hourRef = React.useRef<HTMLInputElement>(null);
    const minuteRef = React.useRef<HTMLInputElement>(null);
    const secondRef = React.useRef<HTMLInputElement>(null);
    const periodRef = React.useRef<HTMLInputElement>(null);
    const segmentRefs: Record<
      DateTimeSegmentKey,
      React.RefObject<HTMLInputElement>
    > = {
      month: monthRef,
      day: dayRef,
      year: yearRef,
      hour: hourRef,
      minute: minuteRef,
      second: secondRef,
      period: periodRef,
    };

    const focusSegment = (key: DateTimeSegmentKey) =>
      segmentRefs[key].current?.focus();

    // Re-sync from an external (controlled) `dateTime` prop unless a segment is
    // mid-edit, matching the same guard TimePickerField uses for its `value` prop.
    React.useEffect(() => {
      if (dateTime === undefined) return;
      const isEditingASegment = Object.values(segmentRefs).some(
        (ref) => ref.current === document.activeElement
      );
      if (isEditingASegment) return;
      const nextDate = new Date(dateTime);
      const nextTimeValue = formatTimeFromDate(
        dateTime,
        use12Hour,
        variantConfig.precision
      );
      setInternalDate(nextDate);
      setTimeValue(nextTimeValue);
      setSegments({
        ...EMPTY_DATE_TIME_SEGMENTS,
        ...segmentsFromDate(nextDate),
        ...segmentsFromTime(nextTimeValue, use12Hour, variantConfig.precision),
      });
      setMonthTextBuffer('');
      setHasInvalidDate(false);
      // Depend on the timestamp, not the Date reference, so a parent that constructs a
      // new-but-equal Date every render doesn't retrigger this sync on every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateTime?.getTime()]);

    const freshFocusRef = React.useRef<Set<DateTimeSegmentKey>>(new Set());
    const markSegmentFocused = (key: DateTimeSegmentKey) => {
      freshFocusRef.current.add(key);
    };
    const consumeFreshFocus = (key: DateTimeSegmentKey) =>
      freshFocusRef.current.delete(key);

    const advanceFocus = (key: DateTimeSegmentKey) => {
      const index = segmentOrder.indexOf(key);
      if (index >= 0 && index < segmentOrder.length - 1) {
        focusSegment(segmentOrder[index + 1]);
      }
    };

    const retreatFocus = (key: DateTimeSegmentKey) => {
      const index = segmentOrder.indexOf(key);
      if (index > 0) {
        focusSegment(segmentOrder[index - 1]);
      }
    };

    const applySegmentSideEffects = (next: DateTimeSegmentState) => {
      if (Object.values(next).every((value) => !value)) {
        setHasInvalidDate(false);
        if (internalDate !== undefined || timeValue !== '') {
          setInternalDate(undefined);
          setTimeValue('');
          onValueChange?.(undefined);
        }
        return;
      }

      let nextDate = internalDate;
      let dateChanged = false;

      const dateResult = buildDateFromSegments(next);
      if (dateResult) {
        setHasInvalidDate(!dateResult.isRealDate);
        if (dateResult.isRealDate) {
          nextDate = dateResult.date;
          dateChanged = true;
          setInternalDate(dateResult.date);
        }
      }

      let nextTimeValue = timeValue;
      let timeChanged = false;

      const timeComplete =
        next.hour.length === 2 &&
        next.minute.length === 2 &&
        (variantConfig.precision !== 'second' || next.second.length === 2) &&
        (!use12Hour || Boolean(next.period));

      if (timeComplete) {
        nextTimeValue = buildTimeString(
          next,
          use12Hour,
          variantConfig.precision
        );
        timeChanged = true;
        setTimeValue(nextTimeValue);
      }

      if (dateChanged || timeChanged) {
        emitChange(nextDate, nextTimeValue);
      }
    };

    const updateSegmentValue = (key: DateTimeSegmentKey, value: string) => {
      const next = { ...segments, [key]: value };
      setSegments(next);
      applySegmentSideEffects(next);
    };

    const handleNumericSegmentKeyDown = (
      key: Exclude<DateTimeSegmentKey, 'period' | 'month'>,
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      const isFresh = consumeFreshFocus(key);
      const action = resolveNumericSegmentKeyAction({
        key: event.key,
        currentValue: segments[key],
        isFresh,
        def: segmentDefs[key],
        advanceKeys: ['/', ':'],
      });

      if (action.type === 'ignore') return;
      event.preventDefault();

      switch (action.type) {
        case 'digit':
          updateSegmentValue(key, action.value);
          if (action.complete) advanceFocus(key);
          return;
        case 'backspaceValue':
          updateSegmentValue(key, action.value);
          return;
        case 'backspaceRetreat':
        case 'retreat':
          retreatFocus(key);
          return;
        case 'clear':
          updateSegmentValue(key, '');
          return;
        case 'advance':
          advanceFocus(key);
          return;
        case 'block':
          return;
      }
    };

    const handleMonthKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      const isFresh = consumeFreshFocus('month');
      const action = resolveMonthSegmentKeyAction({
        key: event.key,
        monthValue: segments.month,
        monthTextBuffer,
        isFresh,
        monthDef: segmentDefs.month,
      });

      if (action.type === 'ignore') return;
      event.preventDefault();

      switch (action.type) {
        case 'digit':
          if (monthTextBuffer) setMonthTextBuffer('');
          updateSegmentValue('month', action.value);
          if (action.complete) advanceFocus('month');
          return;
        case 'setBuffer':
        case 'backspaceBuffer':
          setMonthTextBuffer(action.buffer);
          return;
        case 'commitFromBuffer':
          setMonthTextBuffer('');
          updateSegmentValue('month', action.value);
          advanceFocus('month');
          return;
        case 'backspaceValue':
          updateSegmentValue('month', action.value);
          return;
        case 'backspaceRetreat':
        case 'retreat':
          retreatFocus('month');
          return;
        case 'clear':
          setMonthTextBuffer('');
          updateSegmentValue('month', '');
          return;
        case 'advance':
          advanceFocus('month');
          return;
        case 'block':
          return;
      }
    };

    const handlePeriodKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (/^[apAP]$/.test(event.key)) {
        event.preventDefault();
        updateSegmentValue(
          'period',
          event.key.toUpperCase() === 'A' ? 'AM' : 'PM'
        );
        advanceFocus('period');
        return;
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        updateSegmentValue('period', segments.period === 'AM' ? 'PM' : 'AM');
        return;
      }

      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.preventDefault();
        if (segments.period) {
          updateSegmentValue('period', '');
        } else {
          retreatFocus('period');
        }
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        retreatFocus('period');
        return;
      }

      if (!SEGMENT_NAVIGATION_ALLOWED_KEYS.includes(event.key)) {
        event.preventDefault();
      }
    };

    const handleDateChange = (nextDate: Date | undefined) => {
      setInternalDate(nextDate);
      setSegments((prev) => ({ ...prev, ...segmentsFromDate(nextDate) }));
      setMonthTextBuffer('');
      setHasInvalidDate(false);
      emitChange(nextDate, timeValue);
    };

    const handleTimeChange = (nextTime: string) => {
      setTimeValue(nextTime);
      if (nextTime) {
        setSegments((prev) => ({
          ...prev,
          ...segmentsFromTime(nextTime, use12Hour, variantConfig.precision),
        }));
      }
      emitChange(internalDate, nextTime);
    };

    const handleTimePartsChange = (parts: TimeParts) => {
      setSegments((prev) => ({
        ...prev,
        ...segmentsFromTimeParts(parts, use12Hour, variantConfig.precision),
      }));
    };

    const clear = () => {
      setInternalDate(undefined);
      setTimeValue('');
      setSegments(EMPTY_DATE_TIME_SEGMENTS);
      setMonthTextBuffer('');
      setHasInvalidDate(false);
      onValueChange?.(undefined);
    };

    const hasValue = hasAnySegmentValue(segments, monthTextBuffer);

    const timePanelWidthClass =
      variantConfig.popoverLayout === 'four-column-grid'
        ? 'tw:min-w-[298px]'
        : variantConfig.popoverLayout === 'single-column-list'
          ? 'tw:min-w-[130px]'
          : 'tw:min-w-[220px]';

    const timeContentWidthClass =
      variantConfig.popoverLayout === 'four-column-grid'
        ? 'tw:w-[280px]'
        : undefined;

    const columnViewportHeightClassName =
      variantConfig.popoverLayout === 'columns'
        ? 'tw:h-80'
        : variantConfig.popoverLayout === 'single-column-list'
          ? 'tw:h-80'
          : variantConfig.popoverLayout === 'four-column-grid'
            ? 'tw:h-72'
            : undefined;

    // Subtract the panel's own border plus (for four-column-grid) its header row so the
    // inner scroll viewport matches the calendar's height instead of a fixed value.
    const columnViewportHeightPx = calendarHeight
      ? Math.max(
          40,
          calendarHeight -
            (variantConfig.popoverLayout === 'four-column-grid' ? 60 : 2)
        )
      : undefined;

    const { resolved: monthResolved, displayText: monthDisplayText } =
      resolveMonthDisplay(segments.month, monthTextBuffer);

    const renderMonthSegment = () => (
      <Input
        key="month"
        ref={monthRef}
        id={id}
        type="text"
        autoComplete="off"
        data-slot="input-group-control"
        aria-label="Month"
        value={
          monthResolved && monthFormat === 'numeric'
            ? segments.month
            : monthResolved
              ? monthDisplayText
              : 'mm'
        }
        onChange={() => {}}
        onFocus={(event) => {
          event.currentTarget.select();
          markSegmentFocused('month');
        }}
        onKeyDown={handleMonthKeyDown}
        onPaste={(event) => event.preventDefault()}
        disabled={disabled}
        aria-invalid={hasInvalidDate || externalInvalid || undefined}
        style={{
          width: segmentWidthPx(
            monthResolved && monthFormat === 'long'
              ? monthDisplayText.length
              : 2
          ),
          ...(!monthResolved && { width: 30 }),
        }}
        className={SEGMENT_INPUT_CLASS_NAME}
      />
    );

    const renderNumericSegment = (
      key: Exclude<DateTimeSegmentKey, 'period' | 'month'>,
      label: string
    ) => (
      <Input
        key={key}
        ref={segmentRefs[key]}
        type="text"
        autoComplete="off"
        data-slot="input-group-control"
        inputMode="numeric"
        aria-label={label}
        value={
          segments[key] ||
          (key === 'day'
            ? 'dd'
            : key === 'year'
              ? 'yyyy'
              : key === 'hour' || key === 'minute' || key === 'second'
                ? '--'
                : '')
        }
        onChange={() => {}}
        onFocus={(event) => {
          event.currentTarget.select();
          markSegmentFocused(key);
        }}
        onKeyDown={(event) => handleNumericSegmentKeyDown(key, event)}
        onPaste={(event) => event.preventDefault()}
        disabled={disabled}
        aria-invalid={
          (key === 'day' || key === 'year') &&
          (hasInvalidDate || externalInvalid)
            ? true
            : undefined
        }
        style={{ width: segmentWidthPx(segmentDefs[key].length) }}
        className={SEGMENT_INPUT_CLASS_NAME}
      />
    );

    const renderSeparator = (symbol: string, key: string) => (
      <span key={key} className="tw:select-none tw:text-muted-foreground">
        {symbol}
      </span>
    );

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={(nextOpen) => setIsPopoverOpen(nextOpen)}
      >
        <InputGroup
          ref={mergeRefs(inputGroupRef, forwardedRef)}
          data-slot="date-time-picker"
          className={cn(
            'tw:h-9 tw:w-[325px] tw:rounded-lg tw:border-border tw:bg-background tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)]',
            className
          )}
          {...rest}
        >
          <PopoverTrigger
            render={
              <InputGroupButton
                type="button"
                size="icon-sm"
                disabled={disabled}
                aria-label="Open date and time picker"
              >
                <CalendarDaysIcon className="tw:text-muted-foreground" />
              </InputGroupButton>
            }
          />
          {/* Click-to-focus is a convenience for sighted mouse users; keyboard users tab to segments directly. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="group"
            aria-label="Date and time"
            className="tw:flex tw:flex-1 tw:items-center tw:pl-3 tw:pr-1.5 tw:py-2 tw:text-sm tw:font-normal"
            onClick={(event) => {
              if (event.target !== event.currentTarget) return;
              focusSegment(
                segmentOrder.find((key) => !segments[key]) ?? segmentOrder[0]
              );
            }}
          >
            {renderMonthSegment()}
            {monthResolved && monthFormat === 'long' ? (
              <span className="tw:w-0.5" />
            ) : (
              renderSeparator('/', 'sep-month-day')
            )}
            {renderNumericSegment('day', 'Day')}
            {monthResolved && monthFormat === 'long'
              ? renderSeparator(',', 'sep-day-year')
              : renderSeparator('/', 'sep-day-year')}
            {renderNumericSegment('year', 'Year')}
            <span className="tw:w-2" />
            {renderNumericSegment('hour', 'Hour')}
            {renderSeparator(':', 'sep-hour-minute')}
            {renderNumericSegment('minute', 'Minute')}
            {variantConfig.precision === 'second' && (
              <>
                {renderSeparator(':', 'sep-minute-second')}
                {renderNumericSegment('second', 'Second')}
              </>
            )}
            {use12Hour && (
              <>
                <span className="tw:w-0.5" />
                <Input
                  ref={periodRef}
                  type="text"
                  autoComplete="off"
                  data-slot="input-group-control"
                  aria-label="AM or PM"
                  value={segments.period || '--'}
                  onChange={() => {}}
                  onFocus={(event) => event.currentTarget.select()}
                  onKeyDown={handlePeriodKeyDown}
                  onPaste={(event) => event.preventDefault()}
                  disabled={disabled}
                  style={{ width: segmentWidthPx(3) }}
                  className={SEGMENT_INPUT_CLASS_NAME}
                />
              </>
            )}
          </div>
          {hasValue ? (
            <InputGroupButton
              type="button"
              size="icon-sm"
              aria-label="Clear date and time"
              disabled={disabled}
              onClick={clear}
            >
              <XIcon className="tw:text-muted-foreground" />
            </InputGroupButton>
          ) : (
            <PopoverTrigger
              render={
                <InputGroupButton
                  type="button"
                  size="icon-sm"
                  disabled={disabled}
                  aria-label="Open date and time picker"
                >
                  <ChevronDownIcon className="tw:text-muted-foreground" />
                </InputGroupButton>
              }
            />
          )}
        </InputGroup>
        <PopoverContent
          anchor={inputGroupRef}
          className="tw:w-auto tw:border-0 tw:p-0"
          align="start"
        >
          <div className="tw:flex tw:gap-3 tw:p-2">
            <div ref={calendarWrapperRef} className="tw:self-start">
              <Calendar
                mode="single"
                selected={internalDate}
                onSelect={handleDateChange}
                defaultMonth={internalDate}
                captionLayout={captionLayout}
                navigationVariant={navigationVariant}
                fixedWeeks={fixedWeeks}
              />
            </div>
            <div
              className={cn(
                'tw:flex tw:items-center tw:justify-center tw:self-start tw:rounded-xl tw:border tw:border-border tw:bg-background tw:shadow-[0_1px_3px_rgb(0_0_0/0.10)]',
                timePanelWidthClass
              )}
              style={calendarHeight ? { height: calendarHeight } : undefined}
            >
              <TimePicker
                value={timeValue}
                parts={timePartsForPopover}
                onValueChange={handleTimeChange}
                onTimePartsChange={handleTimePartsChange}
                step={variantConfig.step}
                use12Hour={use12Hour}
                precision={variantConfig.precision}
                popoverLayout={variantConfig.popoverLayout}
                className={timeContentWidthClass}
                columnViewportHeightClassName={columnViewportHeightClassName}
                viewportHeightPx={columnViewportHeightPx}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

export { DateTimePicker };
export type { DateTimePickerProps, DateTimePickerVariant };
